import React, { useCallback, useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import {
  getCalendarIntegrationPreferences,
  getDeviceCalendarStatus,
  isDeviceCalendarSupported,
  listDeviceCalendars,
  openDeviceCalendarSettings,
  requestDeviceCalendarAccess,
  saveCalendarIntegrationPreferences,
} from "../services/externalCalendarService";
import { isAndroidPlatform } from "../utils/platformFeatures";

function CalendarIntegrationSettingsPage({ onPreferencesChanged, onRefresh, onImportAll, refreshing = false, importing = false, importableCount = 0 }) {
  const { lang } = useLanguage();
  const en = lang === "en";
  const [status, setStatus] = useState("loading");
  const [calendars, setCalendars] = useState([]);
  const [preferences, setPreferences] = useState(getCalendarIntegrationPreferences);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const supported = isDeviceCalendarSupported();
  const android = isAndroidPlatform();

  const persist = useCallback((patch) => {
    const next = saveCalendarIntegrationPreferences({ ...preferences, ...patch });
    setPreferences(next);
    onPreferencesChanged?.(next);
    return next;
  }, [onPreferencesChanged, preferences]);

  const loadCalendars = useCallback(async () => {
    const result = await listDeviceCalendars();
    const items = Array.isArray(result.calendars) ? result.calendars : [];
    setCalendars(items);
    setStatus("granted");
    setPreferences((current) => {
      const availableIds = new Set(items.map((item) => String(item.id)));
      const validSelectedIds = current.selectedCalendarIds.filter((id) => availableIds.has(String(id)));
      if (current.selectedCalendarIds.length && validSelectedIds.length !== current.selectedCalendarIds.length) {
        const next = saveCalendarIntegrationPreferences({
          ...current,
          enabled: validSelectedIds.length > 0,
          selectedCalendarIds: validSelectedIds,
        });
        onPreferencesChanged?.(next);
        return next;
      }
      if (current.selectedCalendarIds.length || !items.length) return current;
      const next = saveCalendarIntegrationPreferences({
        ...current,
        enabled: true,
        selectedCalendarIds: items.map((item) => String(item.id)),
      });
      onPreferencesChanged?.(next);
      return next;
    });
  }, [onPreferencesChanged]);

  useEffect(() => {
    let cancelled = false;
    if (!supported) {
      setStatus("unsupported");
      return undefined;
    }
    getDeviceCalendarStatus()
      .then(async (result) => {
        if (cancelled) return;
        const nextStatus = result.status || "notDetermined";
        setStatus(nextStatus);
        if (nextStatus === "fullAccess") await loadCalendars();
      })
      .catch(() => !cancelled && setStatus("unavailable"));
    return () => { cancelled = true; };
  }, [loadCalendars, supported]);

  const requestAccess = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await requestDeviceCalendarAccess();
      setStatus(result.status || (result.granted ? "fullAccess" : "denied"));
      if (result.granted) {
        await loadCalendars();
        setMessage(en ? "Calendar access is ready." : "Takvim erişimi hazır.");
      }
    } catch (error) {
      setMessage(error?.message || (en ? "Calendar access could not be requested." : "Takvim erişimi istenemedi."));
    } finally {
      setBusy(false);
    }
  };

  const toggleCalendar = (calendarId) => {
    const id = String(calendarId);
    const selected = preferences.selectedCalendarIds.includes(id)
      ? preferences.selectedCalendarIds.filter((value) => value !== id)
      : [...preferences.selectedCalendarIds, id];
    persist({ selectedCalendarIds: selected, enabled: selected.length > 0 });
  };

  const permissionDenied = status === "denied" || status === "restricted" || status === "writeOnly";

  return (
    <div className="pageContent calendarIntegrationPage noSwipeBack">
      <section className="calendarIntegrationHero">
        <span className="calendarIntegrationHeroIcon" aria-hidden="true">📅</span>
        <div>
          <h2>{en ? "Calendar integrations" : "Takvim entegrasyonları"}</h2>
          <p>{en
            ? "Show selected device calendars in Family Planner without uploading their contents to our servers."
            : "Seçtiğiniz cihaz takvimlerini, içeriklerini sunucularımıza yüklemeden Aile Yönetimi takviminde gösterin."}</p>
        </div>
      </section>

      <section className="calendarIntegrationCard">
        <div className="calendarIntegrationCardHeader">
          <div>
            <strong>{en ? "Device calendars" : "Cihaz takvimleri"}</strong>
            <span>{supported
              ? (android ? "Google Calendar · Outlook · Exchange · Android" : "Apple Calendar · iCloud · Google · Outlook · Exchange")
              : (en ? "Available in the iPhone/iPad and Android apps" : "iPhone/iPad ve Android uygulamalarında kullanılabilir")}</span>
          </div>
          <span className={`calendarIntegrationStatus calendarIntegrationStatus--${status}`}>
            {status === "fullAccess" || status === "granted" ? (en ? "Connected" : "Bağlı") :
              status === "loading" ? (en ? "Checking" : "Kontrol ediliyor") :
                permissionDenied ? (en ? "Permission required" : "İzin gerekli") : (en ? "Not connected" : "Bağlı değil")}
          </span>
        </div>

        {!supported && (
          <p className="calendarIntegrationNotice">{en
            ? "Web browsers cannot read calendars stored on your device. Open this page in the iOS or Android app to connect them."
            : "Web tarayıcıları cihazınızdaki takvimleri okuyamaz. Bağlamak için bu sayfayı iOS veya Android uygulamasında açın."}</p>
        )}

        {supported && status !== "fullAccess" && status !== "granted" && (
          <div className="calendarIntegrationPermission">
            <p>{en
              ? "Family Planner needs calendar access to read and display your existing events. Access is requested only after you tap the button below. Events are not uploaded automatically."
              : "Aile Yönetimi, mevcut etkinliklerinizi okuyup gösterebilmek için takvim erişimine ihtiyaç duyar. İzin yalnızca aşağıdaki düğmeye dokunduğunuzda istenir. Etkinlikler otomatik olarak sunucuya yüklenmez."}</p>
            {permissionDenied ? (
              <button type="button" className="calendarIntegrationPrimary" onClick={() => openDeviceCalendarSettings()}>
                {en ? `Open ${android ? "Android" : "iOS"} Settings` : `${android ? "Android" : "iOS"} Ayarlarını Aç`}
              </button>
            ) : (
              <button type="button" className="calendarIntegrationPrimary" disabled={busy || status === "loading"} onClick={requestAccess}>
                {busy ? (en ? "Requesting…" : "İzin isteniyor…") : (en ? "Connect device calendars" : "Cihaz takvimlerine bağlan")}
              </button>
            )}
          </div>
        )}

        {(status === "fullAccess" || status === "granted") && (
          <>
            <label className="calendarIntegrationMaster">
              <span>
                <strong>{en ? "Show device events" : "Cihaz etkinliklerini göster"}</strong>
                <small>{en ? "Read-only; your original events cannot be edited here." : "Salt okunur; özgün etkinlikler burada değiştirilemez."}</small>
              </span>
              <input type="checkbox" checked={preferences.enabled} onChange={(event) => persist({ enabled: event.target.checked })} />
            </label>

            <div className="calendarIntegrationList">
              {calendars.map((calendar) => (
                <label key={calendar.id} className="calendarIntegrationRow">
                  <span className="calendarIntegrationColor" style={{ backgroundColor: calendar.color || "#64748b" }} />
                  <span className="calendarIntegrationRowText">
                    <strong>{calendar.title}</strong>
                    <small>{calendar.sourceTitle || (calendar.allowsContentModifications ? "" : (en ? "Read only" : "Salt okunur"))}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={preferences.selectedCalendarIds.includes(String(calendar.id))}
                    onChange={() => toggleCalendar(calendar.id)}
                  />
                </label>
              ))}
              {!calendars.length && <p className="calendarIntegrationNotice">{en ? "No available calendars were found on this device." : "Bu cihazda kullanılabilir takvim bulunamadı."}</p>}
            </div>
            <div className="calendarIntegrationSyncRow">
              <span>{preferences.lastSyncedAt
                ? `${en ? "Last refreshed" : "Son yenileme"}: ${new Date(preferences.lastSyncedAt).toLocaleString(en ? "en-US" : "tr-TR")}`
                : (en ? "Not refreshed yet" : "Henüz yenilenmedi")}</span>
              <button
                type="button"
                className="darkSecondaryBtn"
                disabled={refreshing || !preferences.enabled}
                onClick={async () => {
                  await onRefresh?.();
                  setPreferences(getCalendarIntegrationPreferences());
                }}
              >
                {refreshing ? (en ? "Refreshing…" : "Yenileniyor…") : (en ? "Refresh now" : "Şimdi yenile")}
              </button>
            </div>
            <div className="calendarIntegrationImportBox">
              <div><strong>{en ? "Copy to family calendar" : "Aile takvimine aktar"}</strong><span>{en ? `${importableCount} new device events are ready to copy.` : `${importableCount} yeni cihaz etkinliği aktarılmaya hazır.`}</span></div>
              <button type="button" className="calendarIntegrationPrimary" disabled={importing || !preferences.enabled || !importableCount} onClick={onImportAll}>
                {importing
                  ? (en ? "Copying…" : "Aktarılıyor…")
                  : !importableCount
                    ? (en ? "Nothing to copy" : "Aktarılacak etkinlik yok")
                    : (en ? "Copy all" : "Tümünü aktar")}
              </button>
            </div>
          </>
        )}
        {message && <p className="calendarIntegrationMessage" role="status">{message}</p>}
      </section>

      <section className="calendarIntegrationPrivacy">
        <strong>🔒 {en ? "Private by default" : "Varsayılan olarak gizli"}</strong>
        <p>{en
          ? "Device events are read locally. Only an event you explicitly copy is sent to the family calendar and shared with permitted family members."
          : "Cihaz etkinlikleri yerel olarak okunur. Yalnızca açıkça aktardığınız etkinlik aile takvimine gönderilir ve yetkili aile üyeleriyle paylaşılır."}</p>
      </section>
    </div>
  );
}

export default CalendarIntegrationSettingsPage;
